/**
 * Login route
 * Passwordless authentication page
 */

import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { useLogin } from '@/hooks/useAuth';
import { requireGuest } from '@/lib/auth/routeGuards';

export const Route = createFileRoute('/login')({
  beforeLoad: requireGuest,
  component: LoginComponent,
});

function LoginComponent() {
  const { t } = useTranslation();
  const loginMutation = useLogin();

  const form = useForm({
    defaultValues: {
      name: '',
    },
    onSubmit: async ({ value }) => {
      await loginMutation.mutateAsync({ name: value.name });
    },
  });

  const isLoading = loginMutation.isPending;
  const error = loginMutation.error;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">{t('auth.welcome')}</h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.enterName')}
          </p>
        </div>

        {/* Show welcome message for new users */}
        {loginMutation.isSuccess && loginMutation.data?.isNewUser && (
          <div className="rounded-md bg-green-50 p-4" role="alert">
            <p className="text-sm text-green-800">
              {t('auth.welcomeNewUser')}
            </p>
          </div>
        )}

        {/* Show error message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4" role="alert">
            <p className="text-sm text-red-800">
              {error instanceof Error
                ? error.message
                : t('auth.loginFailed')}
            </p>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="mt-8 space-y-6"
        >
          <form.Field name="name">
            {(field) => (
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth.yourName')}
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  disabled={isLoading}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder={t('auth.enterFirstName')}
                  autoComplete="given-name"
                  autoFocus
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('app.loading')}
              </span>
            ) : (
              t('app.continue')
            )}
          </button>
        </form>

        <p className="mt-4 text-xs text-center text-gray-500">
          {t('auth.noPasswordRequired')}
        </p>
      </div>
    </div>
  );
}
